import config from "../../config";
import * as Sentry from "@sentry/react";

// Cache for storing fetched words
const wordsCache = {
  commonWords: null,
  commonWordsWithSynonyms: null,
  contextWords: {},
  contextWordsWithSynonyms: {},
  loading: {
    common: false,
    contexts: {}
  }
};

const getContextFromLocation = (location) => {
  if (!location) return null;
  const locationLower = location.toLowerCase();
  if (locationLower.includes('vocab') || locationLower.includes('cognate') || locationLower.includes('phrase')) {
    return 'vocabulary';
  }
  if (locationLower.includes('story')) {
    return 'story';
  }
  if (locationLower.includes('assignment')) {
    return 'assignment';
  }
  return null;
};

const getUrls = (context) => {
  const isDev = config.isDev;
  const baseUrl = 'https://storage.googleapis.com/yo-americano.appspot.com/json_data/';
  
  if (context === 'common') {
    return {
      wordsOnly: isDev 
        ? `${baseUrl}prohibated_word_test.json`
        : `${baseUrl}prohibated_word.json`,
      wordsWithSynonyms: isDev
        ? `${baseUrl}prohibated_word_with_synonyms_test.json`
        : `${baseUrl}prohibated_word_with_synonyms.json`
    };
  }
  
  return {
    wordsOnly: `${baseUrl}prohibated_word_${context}_${isDev ? 'test' : 'prod'}.json`,
    wordsWithSynonyms: `${baseUrl}prohibated_word_${context}_with_synonyms_${isDev ? 'test' : 'prod'}.json`
  };
};

const fetchWords = async (url, useCache = false) => {
  try {
    const cacheBuster = useCache ? '' : `?t=${Date.now()}`;
    const response = await fetch(`${url}${cacheBuster}`);
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    return Array.isArray(data) ? data.map(item => 
      typeof item === 'string' ? item.toLowerCase() : (item.word || '').toLowerCase()
    ) : [];
  } catch (error) {
    console.error(`Error fetching words from ${url}:`, error);
    return [];
  }
};

const fetchWordsWithSynonyms = async (url, useCache = false) => {
  try {
    const cacheBuster = useCache ? '' : `?t=${Date.now()}`;
    const response = await fetch(`${url}${cacheBuster}`);
    if (!response.ok) {
      return [];
    }
    const data = await response.json();
    return Array.isArray(data) ? data.map(item => ({
      word: typeof item === 'string' ? item.toLowerCase() : (item.word || '').toLowerCase(),
      synonyms: typeof item === 'string' ? [] : (Array.isArray(item.synonyms) ? item.synonyms : [])
    })) : [];
  } catch (error) {
    console.error(`Error fetching words with synonyms from ${url}:`, error);
    return [];
  }
};

// Prefetch common words in parallel
const prefetchCommonWords = async () => {
  if (wordsCache.loading.common) {
    // Wait for ongoing fetch
    while (wordsCache.loading.common) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    return;
  }

  if (wordsCache.commonWords !== null && wordsCache.commonWordsWithSynonyms !== null) {
    return; // Already cached
  }

  wordsCache.loading.common = true;
  try {
    const commonUrls = getUrls('common');
    const [words, wordsWithSynonyms] = await Promise.all([
      fetchWords(commonUrls.wordsOnly),
      fetchWordsWithSynonyms(commonUrls.wordsWithSynonyms)
    ]);
    
    wordsCache.commonWords = words;
    wordsCache.commonWordsWithSynonyms = wordsWithSynonyms;
  } catch (error) {
    console.error('Error prefetching common words:', error);
  } finally {
    wordsCache.loading.common = false;
  }
};

// Prefetch context-specific words in parallel
const prefetchContextWords = async (context) => {
  if (!context) return;

  if (wordsCache.loading.contexts[context]) {
    // Wait for ongoing fetch
    while (wordsCache.loading.contexts[context]) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    return;
  }

  if (wordsCache.contextWords[context] && wordsCache.contextWordsWithSynonyms[context]) {
    return; // Already cached
  }

  wordsCache.loading.contexts[context] = true;
  try {
    const contextUrls = getUrls(context);
    const [words, wordsWithSynonyms] = await Promise.all([
      fetchWords(contextUrls.wordsOnly),
      fetchWordsWithSynonyms(contextUrls.wordsWithSynonyms)
    ]);
    
    wordsCache.contextWords[context] = words;
    wordsCache.contextWordsWithSynonyms[context] = wordsWithSynonyms;
  } catch (error) {
    console.error(`Error prefetching ${context} words:`, error);
  } finally {
    wordsCache.loading.contexts[context] = false;
  }
};

// Prefetch all words (common + all contexts) in parallel
export const prefetchAllWords = async () => {
  const contexts = ['vocabulary', 'story', 'assignment'];
  
  await Promise.all([
    prefetchCommonWords(),
    ...contexts.map(context => prefetchContextWords(context))
  ]);
};

export async function checkInappropriateContent(text, location = null) {
  try {
    // Ensure common words are loaded (use cache if available)
    if (wordsCache.commonWords === null || wordsCache.commonWordsWithSynonyms === null) {
      await prefetchCommonWords();
    }
    
    const commonWords = wordsCache.commonWords || [];
    const commonWordsWithSynonyms = wordsCache.commonWordsWithSynonyms || [];
    
    let contextWords = [];
    let contextWordsWithSynonyms = [];
    
    if (location) {
      const context = getContextFromLocation(location);
      if (context) {
        // Ensure context words are loaded (use cache if available)
        if (!wordsCache.contextWords[context] || !wordsCache.contextWordsWithSynonyms[context]) {
          await prefetchContextWords(context);
        }
        
        contextWords = wordsCache.contextWords[context] || [];
        contextWordsWithSynonyms = wordsCache.contextWordsWithSynonyms[context] || [];
      }
    }
    
    const allInappropriateWords = [...new Set([...commonWords, ...contextWords])];
    const allWordsWithSynonyms = [...commonWordsWithSynonyms, ...contextWordsWithSynonyms];
    
    const textNormalized = text.replace(/[^\w\s]/gi, '').toLowerCase();
    const wordsInText = new Set(textNormalized.split(/\s+/));
    
    const foundWords = allInappropriateWords.filter(word =>
      word && wordsInText.has(word.toLowerCase())
    );
    
    // Map found words with their synonyms
    const foundWordsWithSynonyms = foundWords.map(foundWord => {
      const match = allWordsWithSynonyms.find(item => item.word === foundWord);
      return {
        word: foundWord,
        synonym: match && match.synonyms && match.synonyms.length > 0 ? match.synonyms[0] : null
      };
    });
    
    return {
      containsInappropriate: foundWords.length > 0,
      foundWords: foundWords.filter(Boolean),
      foundWordsWithSynonyms: foundWordsWithSynonyms
    };
  } catch (error) {
    console.error("Error checking inappropriate content:", error);
    Sentry.captureException(`131 Error during word filter check ${error}.`);
    return {
      containsInappropriate: false,
      foundWords: [],
      foundWordsWithSynonyms: []
    };
  }
}
